// src/components/admin/CreateUserPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminShell } from './Sidebar';

export default function CreateUserPage() {
  const navigate = useNavigate();
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  const [form, setForm] = useState({
    login: '',
    fullName: '',
    phone: '',
    id_role: '',
    password: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoadingRoles(true); setLoadErr('');
        const res = await fetch(`${apiBase}v1/roles?sortBy=id&orderBy=asc`);
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Roles HTTP ${res.status} ${res.statusText} ${t}`.trim());
        }
        const data = await res.json();
        if (abort) return;
        setRoles(data);
        if (!form.id_role && data?.length) {
          setForm((s) => ({ ...s, id_role: String(data[0].id) }));
        }
      } catch (e) {
        if (!abort) setLoadErr(e.message || 'Не удалось загрузить роли');
      } finally {
        if (!abort) setLoadingRoles(false);
      }
    })();
    return () => { abort = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaveErr('');
    if (!form.login.trim()) return setSaveErr('Укажите логин');
    if (!form.password) return setSaveErr('Укажите пароль');
    if (form.password !== form.confirmPassword) return setSaveErr('Пароли не совпадают');
    if (!form.id_role) return setSaveErr('Выберите роль');

    setSaving(true);
    try {
      const payload = {
        login: form.login.trim(),
        password: form.password,
        phone_number: form.phone || null,
        id_role: Number.parseInt(form.id_role, 10),
      };
      const res = await fetch(`${apiBase}v1/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Создание пользователя: HTTP ${res.status} ${t}`);
      }
      const created = await res.json();
      const newUserId = created?.id;
      if (!newUserId) throw new Error('Сервер не вернул id пользователя');
      navigate(`/UserProfile/${newUserId}`, { replace: true });
    } catch (err) {
      setSaveErr(err.message || 'Не удалось создать пользователя');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell
      breadcrumbs="Пользователи"
      title="Создать пользователя"
      subtitle="Учётка для доступа в систему. После создания откроется профиль для заполнения ФИО."
      actions={
        <>
          <button type="button" className="adminbtn" onClick={() => navigate(-1)}>Отмена</button>
          <button
            type="submit"
            form="create-user-form"
            className="adminbtn adminbtn--primary"
            disabled={saving || loadingRoles}
          >
            {saving ? 'Создаём…' : 'Создать пользователя'}
          </button>
        </>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          {!!loadErr && <div className="crmpage__alert crmpage__alert--error">{loadErr}</div>}
          {!!saveErr && <div className="crmpage__alert crmpage__alert--error">{saveErr}</div>}

          <form id="create-user-form" className="adminform" onSubmit={onSubmit} style={{ maxWidth: 880 }}>
            <section className="adminform__card">
              <div className="adminform__section-title">Учётная запись</div>
              <div className="adminform__grid">
                <label className="adminform__field">
                  <span className="adminform__label">Логин<span className="req">*</span></span>
                  <input
                    className="adminform__input"
                    name="login" value={form.login} onChange={onChange}
                    placeholder="manager.almaty"
                    required
                  />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Телефон</span>
                  <input
                    className="adminform__input"
                    name="phone" value={form.phone} onChange={onChange}
                    placeholder="+7 7xx xxx xx xx"
                  />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Роль<span className="req">*</span></span>
                  <select
                    className="adminform__select"
                    name="id_role" value={form.id_role} onChange={onChange}
                    disabled={loadingRoles}
                    required
                  >
                    {loadingRoles ? (
                      <option value="">Загрузка…</option>
                    ) : (
                      <>
                        <option value="">— выберите —</option>
                        {roles.map((r) => (
                          <option key={r.id} value={String(r.id)}>{r.name}</option>
                        ))}
                      </>
                    )}
                  </select>
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">ФИО (опционально)</span>
                  <input
                    className="adminform__input"
                    name="fullName" value={form.fullName} onChange={onChange}
                    placeholder="Заполнится в профиле"
                  />
                  <span className="adminform__hint">Имя/Фамилию/Отчество удобнее ввести в профиле.</span>
                </label>
              </div>
            </section>

            <section className="adminform__card">
              <div className="adminform__section-title">Пароль</div>
              <div className="adminform__grid">
                <label className="adminform__field">
                  <span className="adminform__label">Пароль<span className="req">*</span></span>
                  <input
                    type="password"
                    className="adminform__input"
                    name="password" value={form.password} onChange={onChange}
                    placeholder="********"
                    required
                  />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Повторите пароль<span className="req">*</span></span>
                  <input
                    type="password"
                    className="adminform__input"
                    name="confirmPassword" value={form.confirmPassword} onChange={onChange}
                    placeholder="********"
                    required
                  />
                </label>
              </div>
            </section>
          </form>
        </div>
      </div>
    </AdminShell>
  );
}
